import {useEffect, useRef} from "react";
import {useForm, useWatch, type UseFormReturn} from "react-hook-form";
import {toast} from "sonner";

import {Button} from "@/components/ui/button";
import {ComboboxGeneric} from "@/components/ui/combobox-generic";
import DatePicker from "@/components/ui/date-picker";
import {FakeProgress} from "@/components/ui/fake-progress";
import {Form, FormControl, FormField, FormItem, FormLabel} from "@/components/ui/form";
import FormError from "@/components/ui/form-error";
import ImagePicker from "@/components/ui/image-picker";
import {Input} from "@/components/ui/input";
import {Item, ItemContent, ItemDescription, ItemTitle} from "@/components/ui/item";
import {Label} from "@/components/ui/label";
import {Sheet, SheetContent, SheetFooter} from "@/components/ui/sheet";
import {Switch} from "@/components/ui/switch";
import {Textarea} from "@/components/ui/textarea";
import {TOAST_CONFIG} from "@/lib/constansts";
import {getDateTime, moment} from "@/src/modules/core/lib";
import {Loader, CoordinatesRequiredGate} from "@/src/modules/core/components";
import {
  FORM_SHEET_FOOTER_CLASS,
  FORM_SHEET_ROOT_FORM_CLASS,
} from "@/src/modules/core/lib/form-sheet-layout";
import {useFormDraft, type ClearFormDraft} from "@/src/modules/core/hooks/use-form-draft";
import {useRestoreFormDraft} from "@/src/modules/core/hooks/use-restore-form-draft";
import {createFormInvalidHandler} from "@/src/modules/core/lib/glitchtip/product-telemetry";
import {useCoordinatesGate} from "@/src/modules/core/hooks/use-coordinates-gate";
import {resolveGpsForSubmit} from "@/src/modules/core/lib/resolve-gps-coordinates-string";
import {useStoredMeProperties} from "@/src/modules/core/store/me";
import {useStoredUserProperty} from "@/src/modules/core/store/user-property";
import {useCreateFiles} from "@/src/modules/files/hooks";
import {
  deserializeIncidentFormDraftValues,
  getIncidentFormDraftKey,
  incidentFormHasPersistableContent,
  INCIDENT_SHEET_ID,
  serializeIncidentFormDraftValues,
} from "@/src/modules/incidents/lib/incident-form-draft";
import {
  useIncidentReport,
  useIncidentReportCreate,
  useIncidentReportUpdate,
} from "@/src/modules/incidents/hooks";
import {
  INCIDENT_FORM_HELP_KEYS,
  INCIDENT_HELP_TOURS,
} from "@/src/modules/incidents/lib/incident-help";
import {FieldHelpLabel} from "@/src/modules/help/components/field-help";
import {SheetFormScrollBody} from "@/src/modules/help/components/sheet-form-scroll-body";
import {SheetHelpHeader} from "@/src/modules/help/components/sheet-help-header";
import {useCancelHelpTourOnSheetClose} from "@/src/modules/help/hooks/use-cancel-help-tour-on-sheet-close";

import {IncidentTypeCombobox} from "./type-combobox";

const MAX_INCIDENT_DETAILS_LENGTH = 3000;
const MAX_INCIDENT_LOCATION_LENGTH = 400;

const startOfYear = moment().startOf("year").toDate();

type IncidentSheetProps = {
  isVisible: boolean;
  incidentId?: number;
  onClose: () => void;
  draftRestoreValues?: Record<string, unknown> | null;
  onDraftRestored?: () => void;
};

export function IncidentSheet({
  isVisible,
  incidentId,
  onClose,
  draftRestoreValues,
  onDraftRestored,
}: IncidentSheetProps) {
  const formRef = useRef<UseFormReturn<Record<string, unknown>> | null>(null);
  const draftHelpersRef = useRef<{clearDraft: ClearFormDraft} | null>(null);

  const {isPending, incidentReport} = useIncidentReport(incidentId);

  const {mutateAsync: mutateCreateFiles} = useCreateFiles();
  const {mutateAsync: mutateInsertIncidentReport} = useIncidentReportCreate();
  const {mutateAsync: mutateUpdateIncidentReport} = useIncidentReportUpdate();

  useCancelHelpTourOnSheetClose(isVisible);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      return;
    }
    draftHelpersRef.current?.clearDraft();
    onClose();
  };

  const onSubmit = async (data: Record<string, unknown>) => {
    try {
      const filesList = (data.files as unknown[]) ?? [];
      let files = filesList.filter((i) => !(i instanceof File));
      const toProcess = filesList.filter((i) => i instanceof File);

      if (toProcess.length && navigator.onLine) {
        const newFiles = await mutateCreateFiles(toProcess as File[]);
        files = [...files, ...newFiles];
      } else if (!navigator.onLine) {
        files = filesList;
      }

      const gps = await resolveGpsForSubmit(
        {
          propertyCoordinates:
            (data.property as {gps_coordinates?: string | null} | undefined)?.gps_coordinates ??
            null,
        },
        {
          captureContext: {
            feature: "incidents",
            operation: incidentId ? "incident_update" : "incident_create",
          },
        },
      );
      if (!gps) {
        return;
      }

      const incidentData = {
        incident_report_type_id: (data.type as {incident_report_type_id: number})
          .incident_report_type_id,
        incident_date: getDateTime(
          data.incident_date as Date,
          data.incident_time as string,
        ).utc().format(),
        details: data.details,
        gps_coordinates: gps.gps_coordinates,
        gps_source: gps.gps_source,
        send_notification: Number(data.send_notification),
        include_gps_coordinates: Number(data.include_gps_coordinates),
        incident_location: data.incident_location,
        property_id: (data.property as {id: number}).id,
        files,
      };

      if (!navigator.onLine) {
        toast.error(
          "You need an internet connection to submit an incident report.",
          TOAST_CONFIG,
        );
        return;
      }

      if (!incidentId) {
        await mutateInsertIncidentReport(incidentData);
      } else {
        (incidentData as Record<string, unknown>)["incident_report_id"] = incidentId;
        await mutateUpdateIncidentReport(incidentData);
      }

      draftHelpersRef.current?.clearDraft({submitted: true});
      onClose();
    } catch (error) {
      console.log(error);
    }
  };

  const handleInit = (helpers: {
    form: UseFormReturn<Record<string, unknown>>;
    clearDraft: ClearFormDraft;
  }) => {
    formRef.current = helpers.form;
    draftHelpersRef.current = helpers;
  };

  return (
    <Sheet
      open={isVisible}
      askConfirmation={() => {
        if (!formRef.current) return false;
        return Object.keys(formRef.current.formState.dirtyFields).length > 0;
      }}
      onOpenChange={handleOpenChange}
    >
      <SheetContent className="flex min-h-0 flex-col gap-0 overflow-hidden p-0">
        <SheetHelpHeader
          title={incidentId ? "Update Incident report" : "New Incident report"}
          createTourId={INCIDENT_HELP_TOURS.formCreate}
          showCreateTour={!incidentId}
        />
        {isPending ? (
          <Loader className="min-h-48 flex-1" />
        ) : (
          <IncidentForm
            isVisible={isVisible}
            isEditing={!!incidentId}
            incidentId={incidentId}
            incidentReport={incidentReport as Record<string, unknown> | undefined}
            draftRestoreValues={draftRestoreValues}
            onDraftRestored={onDraftRestored}
            onSubmit={onSubmit}
            onInit={handleInit}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function IncidentForm({
  isVisible,
  isEditing,
  incidentId,
  incidentReport,
  draftRestoreValues,
  onDraftRestored,
  onSubmit,
  onInit,
}: {
  isVisible: boolean;
  isEditing: boolean;
  incidentId?: number;
  incidentReport?: Record<string, unknown>;
  draftRestoreValues?: Record<string, unknown> | null;
  onDraftRestored?: () => void;
  onSubmit: (data: Record<string, unknown>) => Promise<void>;
  onInit: (helpers: {
    form: UseFormReturn<Record<string, unknown>>;
    clearDraft: ClearFormDraft;
  }) => void;
}) {
  const skipOpenResetRef = useRef(false);
  const minDateRef = useRef(startOfYear);
  const userProperty = useStoredUserProperty();
  const userProperties = useStoredMeProperties();

  const properties = userProperties.filter((p) => p.is_hidden === 0);
  const hasPropertySelector = properties.length > 1;

  const now = moment();
  const defaultValues = {
    type: incidentReport?.type
      ? {
          incident_report_type_id: (incidentReport.type as {id: number}).id,
          type: (incidentReport.type as {name: string}).name,
        }
      : null,
    details: (incidentReport?.details as string) ?? "",
    incident_date: incidentReport?.incident_date
      ? moment(incidentReport.incident_date as string).toDate()
      : now.toDate(),
    incident_time: incidentReport?.incident_date
      ? moment(incidentReport.incident_date as string).format("HH:mm")
      : now.format("HH:mm"),
    send_notification: (incidentReport?.send_notification as number) ?? 0,
    files: (incidentReport?.files as unknown[]) ?? [],
    include_gps_coordinates: (incidentReport?.include_gps_coordinates as number) ?? 0,
    incident_location: (incidentReport?.incident_location as string) ?? "",
    property: userProperty,
  };

  const form = useForm({
    defaultValues,
  });
  const {
    handleSubmit,
    control,
    formState: {errors, isSubmitting},
    watch,
    setValue,
    getValues,
    reset,
  } = form;

  const files = useWatch({control, name: "files", defaultValue: []});
  const {submitBlockedByLocation} = useCoordinatesGate(true, {active: isVisible});

  const draftKey = getIncidentFormDraftKey(isEditing, incidentId, userProperty?.id);

  const {snapshotDraft, clearDraft} = useFormDraft({
    draftKey,
    moduleLabel: "Incident report",
    route: "/incidents",
    sheetId: INCIDENT_SHEET_ID,
    entityId: incidentId,
    contextPropertyId: userProperty?.id,
    enabled: true,
    getValues,
    serializeValues: serializeIncidentFormDraftValues,
    shouldPersist: () => incidentFormHasPersistableContent(getValues()),
    watch,
    abandonTelemetry: {
      feature: "incidents",
      operation: isEditing ? "incident_update" : "incident_create",
    },
  });

  useRestoreFormDraft({
    draftRestoreValues,
    onRestore: () => {
      reset({
        ...defaultValues,
        ...deserializeIncidentFormDraftValues(draftRestoreValues!),
      });
      skipOpenResetRef.current = true;
      onDraftRestored?.();
    },
  });

  const handleUploadFiles = (newFiles: unknown[]) => {
    setValue("files", newFiles, {shouldDirty: true});
    snapshotDraft();
  };

  const removeFiles = (index: number) => {
    setValue(
      "files",
      getValues("files").filter((_: unknown, i: number) => i !== index),
      {shouldDirty: true},
    );
    snapshotDraft();
  };

  useEffect(() => {
    onInit?.({
      form: form as unknown as UseFormReturn<Record<string, unknown>>,
      clearDraft,
    });
  }, [form, clearDraft, onInit]);

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit(
          onSubmit,
          createFormInvalidHandler({
            feature: "incidents",
            operation: isEditing ? "incident_update" : "incident_create",
            sheetId: INCIDENT_SHEET_ID,
          }),
        )}
        className={FORM_SHEET_ROOT_FORM_CLASS}
      >
        <CoordinatesRequiredGate />
        <SheetFormScrollBody className="flex flex-col gap-4 px-4 pt-2 pb-4">
        <fieldset
          disabled={submitBlockedByLocation}
          className="m-0 flex min-w-0 flex-col gap-4 border-0 p-0"
        >
        {hasPropertySelector ? (
          <FormField
            control={form.control}
            name="property"
            rules={{required: "Property is required"}}
            render={({field, formState: {errors: formErrors}}) => (
              <FormItem>
                <FieldHelpLabel helpKey={INCIDENT_FORM_HELP_KEYS.property} showIcon={false}>
                  <FormLabel>Property</FormLabel>
                </FieldHelpLabel>
                <FormControl>
                  <ComboboxGeneric
                    items={properties}
                    {...field}
                    placeholder="Select property"
                    preserveCase
                    getItemKey={(item) => item.id}
                    getItemLabel={(item) => item.name}
                    isItemEqual={(item, selected) => item.id === selected?.id}
                    renderItem={(option) => (
                      <Item size="sm" className="p-0">
                        <ItemContent>
                          <ItemTitle className="font-normal">{option.name}</ItemTitle>
                          <ItemDescription className="text-xs">
                            {option.street} {option.number}, {option.city}, {option.state}{" "}
                            {option.zipcode}
                          </ItemDescription>
                        </ItemContent>
                      </Item>
                    )}
                  />
                </FormControl>
                {formErrors.property && (
                  <FormError error={formErrors.property.message as string} />
                )}
              </FormItem>
            )}
          />
        ) : null}
        <FormField
          control={control}
          name="type"
          rules={{required: "Type is required"}}
          render={({field}) => (
            <FormItem>
              <FieldHelpLabel helpKey={INCIDENT_FORM_HELP_KEYS.type} showIcon={false}>
                <Label htmlFor="type" className="text-foreground">
                  Type
                </Label>
              </FieldHelpLabel>
              <IncidentTypeCombobox {...field} />
              {errors.type && <FormError error={errors.type.message as string} />}
            </FormItem>
          )}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={control}
            name="incident_date"
            rules={{required: "Date is required"}}
            render={({field}) => (
              <FormItem>
                <FieldHelpLabel helpKey={INCIDENT_FORM_HELP_KEYS.date} showIcon={false}>
                  <Label htmlFor="date_date" className="text-foreground">
                    Date
                  </Label>
                </FieldHelpLabel>
                <DatePicker
                  id="date_date"
                  disabled={{before: minDateRef.current}}
                  selected={field.value}
                  onSelect={field.onChange}
                />
                {errors.incident_date && (
                  <FormError error={errors.incident_date.message as string} />
                )}
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="incident_time"
            render={({field}) => (
              <FormItem>
                <FieldHelpLabel helpKey={INCIDENT_FORM_HELP_KEYS.time} showIcon={false}>
                  <Label htmlFor="incident_time" className="text-foreground">
                    Time
                  </Label>
                </FieldHelpLabel>
                <Input type="time" id="incident_time" {...field} />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={control}
          name="details"
          rules={{
            required: "Details is required",
            maxLength: {
              value: MAX_INCIDENT_DETAILS_LENGTH,
              message: `Details must be less than ${MAX_INCIDENT_DETAILS_LENGTH} characters`,
            },
          }}
          render={({field}) => (
            <FormItem>
              <FieldHelpLabel helpKey={INCIDENT_FORM_HELP_KEYS.details} showIcon={false}>
                <Label htmlFor="details" className="text-foreground">
                  Details
                </Label>
              </FieldHelpLabel>
              <Textarea
                {...field}
                className="resize-none"
                maxLength={MAX_INCIDENT_DETAILS_LENGTH}
                rows={5}
              />
              <p className="ml-auto text-sm text-muted-foreground">
                {(field.value as string)?.length ?? 0} / {MAX_INCIDENT_DETAILS_LENGTH}
              </p>
              {errors.details && <FormError error={errors.details.message as string} />}
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="incident_location"
          rules={{
            maxLength: {
              value: MAX_INCIDENT_LOCATION_LENGTH,
              message: `Incident location must be less than ${MAX_INCIDENT_LOCATION_LENGTH} characters`,
            },
            validate: (value, formData) => {
              const dateToCompare = isEditing
                ? (incidentReport?.created_at as string)
                : moment();
              const isToday = moment(formData.incident_date).isSame(dateToCompare, "day");
              if (!isToday && !value) {
                return "Location is required";
              }
              return true;
            },
          }}
          render={({field}) => (
            <FormItem>
              <FieldHelpLabel helpKey={INCIDENT_FORM_HELP_KEYS.incidentLocation} showIcon={false}>
                <Label htmlFor="incident_location" className="text-foreground">
                  Incident location
                </Label>
              </FieldHelpLabel>
              <Textarea
                {...field}
                className="resize-none"
                maxLength={MAX_INCIDENT_LOCATION_LENGTH}
                rows={5}
              />
              <p className="ml-auto text-sm text-muted-foreground">
                {(field.value as string)?.length ?? 0} / {MAX_INCIDENT_LOCATION_LENGTH}
              </p>
              {errors.incident_location && (
                <FormError error={errors.incident_location.message as string} />
              )}
            </FormItem>
          )}
        />
        <div className="flex flex-col gap-2">
          <FormItem>
            <FieldHelpLabel helpKey={INCIDENT_FORM_HELP_KEYS.images} showIcon={false}>
              <Label htmlFor="files" className="text-foreground">
                Images
              </Label>
            </FieldHelpLabel>
            <ImagePicker
              maxFiles={10}
              images={files ?? []}
              errors={errors}
              immediateUpload
              captureFeature="incidents"
              captureOperation={isEditing ? "incident_update" : "incident_create"}
              onBeforePick={snapshotDraft}
              onUpload={handleUploadFiles}
              onDelete={removeFiles}
            />
            {(files?.length ?? 0) > 0 && isSubmitting ? <FakeProgress /> : null}
          </FormItem>
        </div>
        <div className="flex flex-col gap-2">
          <FormField
            control={control}
            name="send_notification"
            render={({field}) => (
              <FormItem className="flex flex-row items-center justify-between">
                <FieldHelpLabel
                  helpKey={INCIDENT_FORM_HELP_KEYS.sendNotification}
                  watchValue={Boolean(field.value)}
                  showIcon={false}
                >
                  <Label htmlFor="send_notification" className="text-foreground">
                    Send notification to property manager
                  </Label>
                </FieldHelpLabel>
                <FormControl>
                  <Switch
                    id="send_notification"
                    name="send_notification"
                    disabled={isEditing}
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) => field.onChange(checked ? 1 : 0)}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-col gap-2">
          <FormField
            control={control}
            name="include_gps_coordinates"
            render={({field}) => (
              <FormItem className="flex flex-row items-center justify-between">
                <FieldHelpLabel
                  helpKey={INCIDENT_FORM_HELP_KEYS.includeGpsCoordinates}
                  watchValue={Boolean(field.value)}
                  showIcon={false}
                >
                  <Label htmlFor="include_gps_coordinates" className="text-foreground">
                    Include GPS coordinates
                  </Label>
                </FieldHelpLabel>
                <FormControl>
                  <Switch
                    id="include_gps_coordinates"
                    name="include_gps_coordinates"
                    checked={Boolean(field.value)}
                    onCheckedChange={(checked) => field.onChange(checked ? 1 : 0)}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        </fieldset>
        </SheetFormScrollBody>
        <SheetFooter className={FORM_SHEET_FOOTER_CLASS}>
          <Button
            type="submit"
            className="w-full"
            data-help-target={INCIDENT_FORM_HELP_KEYS.submit}
            disabled={isSubmitting || submitBlockedByLocation}
          >
            {isEditing ? "Update Report" : "Submit Report"}
          </Button>
        </SheetFooter>
      </form>
    </Form>
  );
}
